import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Handshake, Building2, Wrench, Users, Truck, Award, Check, AlertCircle, ArrowLeft, ArrowRight, Phone, Mail } from 'lucide-react';
import { api } from '../lib/api';
import useSEO from '../hooks/useSEO';

// 6 partner tracks — kept in sync with Partners.jsx
const PROGRAMS = [
    { key: 'industrial-contractors', name: 'Industrial Contractors', icon: Wrench, desc: 'Add advanced cleaning technology to your service offerings.' },
    { key: 'equipment-dealers', name: 'Equipment Dealers', icon: Truck, desc: 'Distribute Dry Ice Bulls machines through your network.' },
    { key: 'rental-companies', name: 'Rental Companies', icon: Building2, desc: 'Add industrial cleaning equipment to your rental fleet.' },
    { key: 'referral-partners', name: 'Referral Partners', icon: Users, desc: 'Earn commission on qualified referrals.' },
    { key: 'dry-ice-suppliers', name: 'Dry Ice Suppliers', icon: Award, desc: 'Become a preferred CO₂ / dry ice fulfillment partner.' },
    { key: 'sales-representatives', name: 'Sales Representatives', icon: Handshake, desc: 'Independent reps with existing industrial relationships.' },
];

const trackClick = (name, params = {}) => {
    if (typeof window !== 'undefined' && window.trackConversion) window.trackConversion(name, params);
};

const initial = {
    name: '',
    email: '',
    phone: '',
    company: '',
    partner_program: '',
    existing_business: '',
    existing_business_detail: '',
    partner_location: '',
    message: '',
};

const PartnersApply = () => {
    const [form, setForm] = useState(initial);
    const [sending, setSending] = useState(false);
    const [ok, setOk] = useState(false);
    const [err, setErr] = useState('');
    const location = useLocation();

    useSEO({
        title: 'Apply as a Partner — Dry Ice Bulls Cleaning System®',
        description: 'Apply to join the Dry Ice Bulls partner program. Six tracks: industrial contractors, equipment dealers, rental companies, referral partners, CO₂ suppliers, and sales reps.',
    });

    // Pre-select a program from URL param (?program=equipment-dealers)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const p = params.get('program');
        if (p && PROGRAMS.find((x) => x.key === p)) {
            setForm((f) => ({ ...f, partner_program: p }));
        }
    }, [location.search]);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const selectProgram = (key) => {
        setForm({ ...form, partner_program: key });
        trackClick('partner_program_select', { program: key });
    };

    const submit = async (e) => {
        e.preventDefault();
        setErr('');
        if (!form.phone || form.phone.trim().length < 5) {
            setErr('Phone number is required — emails often go to spam and we need a way to reach you.');
            return;
        }
        if (!form.partner_program) {
            setErr('Please pick which partner program you\'re interested in.');
            return;
        }
        setSending(true);
        try {
            const programName = PROGRAMS.find((p) => p.key === form.partner_program)?.name || form.partner_program;
            const payload = {
                name: form.name,
                email: form.email,
                phone: form.phone,
                company: form.company,
                subject: `Partner Application — ${programName}`,
                message: form.message || `New partner application for the ${programName} program.`,
                partner_program: programName,
                existing_business: form.existing_business,
                existing_business_detail: form.existing_business_detail,
                partner_location: form.partner_location,
                // Legacy fields left blank so this doesn't count as a machine-quote or service inquiry
                machine_ref: '',
                industry: '',
                location: form.partner_location, // also fill main "location" for admin dashboard
                preferred_service: '',
            };
            await api.post('/contact', payload);
            trackClick('form_submit', { form_id: 'partner_apply', program: form.partner_program });
            trackClick('partner_apply_submit', { program: form.partner_program });
            setOk(true);
            setForm(initial);
        } catch (e2) {
            const detail = e2.response?.data?.detail;
            const message = Array.isArray(detail)
                ? detail.map((x) => x?.msg || JSON.stringify(x)).join(' ')
                : typeof detail === 'string' ? detail : 'Could not submit. Please call us instead.';
            setErr(message);
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="partners-apply-page">
            {/* Hero */}
            <section className="border-b border-neutral-200 bg-white">
                <div className="container-page pt-32 pb-14">
                    <Link
                        to="/partners"
                        className="inline-flex items-center gap-2 label-caps text-neutral-500 hover:text-neutral-900 transition-colors text-xs mb-6"
                        data-testid="partners-apply-back"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back to Partner Program
                    </Link>
                    <div className="label-caps text-brand-primary mb-4">/ Partner Application</div>
                    <h1 className="font-display font-black uppercase text-5xl md:text-7xl leading-[0.95] text-neutral-900">
                        Apply to become a<br />
                        <span className="text-brand-primary">Dry Ice Bulls</span> partner.
                    </h1>
                    <p className="mt-6 max-w-2xl text-base md:text-lg text-neutral-600 leading-relaxed">
                        Tell us which program fits your operation and whether you&apos;re already in the space. We reply within one business day with next steps.
                    </p>
                </div>
            </section>

            <section className="container-page py-16 md:py-20 bg-white">
                {ok ? (
                    <div className="border border-brand-primary bg-brand-primary/5 p-8 md:p-10 max-w-3xl" data-testid="partners-apply-success">
                        <Check className="h-10 w-10 text-brand-primary mb-4" />
                        <h3 className="font-display font-black uppercase text-2xl md:text-3xl text-neutral-900">Application received</h3>
                        <p className="mt-3 text-neutral-600">
                            Thanks — your partner application is in front of the team. A specialist will call you on the number you provided.
                            If you don&apos;t hear back within one business day, check your spam folder (Resend emails sometimes get filtered).
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <button
                                type="button"
                                onClick={() => { setOk(false); setForm(initial); }}
                                className="label-caps text-brand-primary hover:text-brand-primaryHover transition-colors text-xs"
                                data-testid="partners-apply-send-another"
                            >
                                Submit another application →
                            </button>
                            <Link
                                to="/"
                                className="label-caps text-neutral-500 hover:text-neutral-900 transition-colors text-xs"
                            >
                                Back to homepage →
                            </Link>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12" data-testid="partners-apply-form">
                        {/* LEFT: Program picker */}
                        <div className="lg:col-span-5">
                            <div className="lg:sticky lg:top-28">
                                <div className="label-caps text-brand-primary text-xs mb-3">/ Step 1 — Which program?</div>
                                <h2 className="font-display font-black uppercase text-2xl md:text-3xl text-neutral-900 leading-tight mb-6">
                                    Pick a partner track.
                                </h2>
                                <div className="grid grid-cols-1 gap-2">
                                    {PROGRAMS.map((p) => {
                                        const Icon = p.icon;
                                        const active = form.partner_program === p.key;
                                        return (
                                            <button
                                                key={p.key}
                                                type="button"
                                                onClick={() => selectProgram(p.key)}
                                                className={`text-left border p-4 transition-colors flex items-start gap-3 ${
                                                    active
                                                        ? 'border-brand-primary bg-brand-primary/5'
                                                        : 'border-neutral-200 bg-white hover:border-neutral-400'
                                                }`}
                                                data-testid={`partners-apply-program-${p.key}`}
                                            >
                                                <div className={`inline-flex items-center justify-center w-9 h-9 border flex-shrink-0 ${active ? 'border-brand-primary' : 'border-neutral-200'}`}>
                                                    <Icon className={`h-4 w-4 ${active ? 'text-brand-primary' : 'text-neutral-500'}`} />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className={`font-display font-bold uppercase text-sm leading-tight ${active ? 'text-brand-primary' : 'text-neutral-900'}`}>
                                                        {p.name}
                                                    </div>
                                                    <div className="text-xs text-neutral-500 mt-1 leading-relaxed">{p.desc}</div>
                                                </div>
                                                {active && <Check className="h-4 w-4 text-brand-primary flex-shrink-0 mt-1" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* RIGHT: Form fields */}
                        <div className="lg:col-span-7">
                            <div className="label-caps text-brand-primary text-xs mb-3">/ Step 2 — About you</div>
                            <h2 className="font-display font-black uppercase text-2xl md:text-3xl text-neutral-900 leading-tight mb-6">
                                Tell us about your business.
                            </h2>

                            <div className="space-y-5">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <Field label="Full name *" value={form.name} onChange={update('name')} required testid="pa-name" />
                                    <Field label="Company / business name" value={form.company} onChange={update('company')} testid="pa-company" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <Field label="Email *" type="email" value={form.email} onChange={update('email')} required testid="pa-email" />
                                    <Field
                                        label={<>Phone <span className="text-brand-primary">* required</span></>}
                                        type="tel"
                                        value={form.phone}
                                        onChange={update('phone')}
                                        placeholder="e.g. +1 555 123 4567"
                                        testid="pa-phone"
                                    />
                                </div>

                                <Field
                                    label="Potential partner location (city / state / country)"
                                    value={form.partner_location}
                                    onChange={update('partner_location')}
                                    placeholder="e.g. Raleigh, NC — or a full territory"
                                    testid="pa-location"
                                />

                                {/* Existing business? Yes / No / Considering */}
                                <div>
                                    <label className="label-caps text-neutral-600 block mb-2">
                                        Are you already in this industry / operating this kind of business?
                                    </label>
                                    <div className="grid grid-cols-3 gap-2" data-testid="pa-existing-radio">
                                        {[
                                            { k: 'yes', label: 'Yes, active' },
                                            { k: 'considering', label: 'Considering it' },
                                            { k: 'no', label: 'No, new to me' },
                                        ].map((o) => {
                                            const active = form.existing_business === o.k;
                                            return (
                                                <button
                                                    key={o.k}
                                                    type="button"
                                                    onClick={() => setForm({ ...form, existing_business: o.k })}
                                                    className={`border p-3 label-caps text-xs transition-colors ${active ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-neutral-800 border-neutral-300 hover:border-brand-primary'}`}
                                                    data-testid={`pa-existing-${o.k}`}
                                                >
                                                    {o.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {(form.existing_business === 'yes' || form.existing_business === 'considering') && (
                                    <Field
                                        label={form.existing_business === 'yes'
                                            ? 'Briefly describe what you do today (services, brands, revenue scale, team size…)'
                                            : 'What are you considering — where would you start?'}
                                        value={form.existing_business_detail}
                                        onChange={update('existing_business_detail')}
                                        testid="pa-existing-detail"
                                    />
                                )}

                                <div>
                                    <label className="label-caps text-neutral-600 block mb-2">Anything else we should know?</label>
                                    <textarea
                                        rows={5}
                                        value={form.message}
                                        onChange={update('message')}
                                        className="w-full bg-white border border-neutral-300 focus:border-brand-primary transition-colors text-neutral-900 p-4 outline-none"
                                        placeholder="Territory, target industries, current customer base, timeline, questions for us…"
                                        data-testid="pa-message"
                                    />
                                </div>

                                <div className="text-xs text-neutral-500 border-l-2 border-brand-primary pl-4">
                                    Phone required — some Resend replies get filtered as spam. A phone number lets us confirm receipt quickly.
                                </div>

                                {err && (
                                    <div className="flex items-start gap-3 border border-red-300 bg-red-50 text-red-700 p-4" data-testid="partners-apply-error">
                                        <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                                        <div>{err}</div>
                                    </div>
                                )}

                                <div className="flex flex-wrap items-center gap-3 pt-2">
                                    <button
                                        type="submit"
                                        disabled={sending}
                                        className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-white font-bold px-7 py-4 label-caps transition-colors"
                                        data-testid="partners-apply-submit"
                                    >
                                        {sending ? 'Sending…' : (<>Submit Application <ArrowRight className="h-4 w-4" /></>)}
                                    </button>
                                    <a
                                        href="tel:+19842010767"
                                        onClick={() => trackClick('phone_click', { location: 'partners_apply' })}
                                        className="inline-flex items-center gap-2 border border-neutral-300 hover:border-neutral-900 text-neutral-900 px-6 py-4 label-caps transition-colors"
                                        data-testid="partners-apply-call"
                                    >
                                        <Phone className="h-4 w-4" /> Prefer to talk? (984) 201-0767
                                    </a>
                                </div>
                            </div>
                        </div>
                    </form>
                )}
            </section>
        </div>
    );
};

const Field = ({ label, testid, ...props }) => (
    <div>
        <label className="label-caps text-neutral-600 block mb-2">{label}</label>
        <input
            {...props}
            className="w-full bg-white border border-neutral-300 focus:border-brand-primary transition-colors text-neutral-900 p-4 outline-none"
            data-testid={testid}
        />
    </div>
);

export default PartnersApply;
